import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ImageStoryPanelRetryButton } from '@/components/narration/ImageStoryPanelRetryButton';

function frameCode(number, index) {
  const base = String(number ?? index + 1).padStart(2, '0');
  return index === 0 ? `${base}A` : base;
}

function FilmReelFrame({
  item,
  index,
  total,
  expanded,
  onToggleExpand,
  onSelect,
  onMove,
  orientation,
  disabled,
  renderExtra,
  onRetryItem,
  imageRetryBusy = null,
}) {
  const frameLabel = item.frameLabel || frameCode(item.frameNumber ?? item.number, index);
  const hasScript = item.sections?.some((s) => s.content);

  return (
    <article
      className={`cinematic-film-reel__frame ${expanded ? 'cinematic-film-reel__frame--expanded' : ''}`}
      data-testid={item.testId || `film-reel-frame-${frameLabel}`}
    >
      <div className="cinematic-film-reel__frame-inner">
        <button
          type="button"
          className="cinematic-film-reel__frame-head"
          onClick={() => {
            onToggleExpand(item.id);
            onSelect?.(item);
          }}
          aria-expanded={expanded}
        >
          <span className="cinematic-film-reel__frame-code">{frameLabel}</span>
          <span className="cinematic-film-reel__frame-meta">
            {item.statusLabel ? (
              <span className={`cinematic-film-reel__status cinematic-film-reel__status--${item.status || 'default'}`}>
                {item.statusLabel}
              </span>
            ) : null}
            <span className="cinematic-film-reel__frame-title">
              {item.title || `Scene ${item.frameNumber ?? index + 1}`}
            </span>
          </span>
          <ChevronDown
            className={`cinematic-film-reel__chevron ${expanded ? 'cinematic-film-reel__chevron--open' : ''}`}
          />
        </button>

        <div className="cinematic-film-reel__aperture">
          {item.imageUrl ? (
            <div className="cinematic-film-reel__thumb-wrap relative">
              <button
                type="button"
                className="cinematic-film-reel__thumb-btn"
                onClick={() => onSelect?.(item)}
                disabled={disabled}
              >
                <img
                  src={item.imageUrl}
                  alt={item.title || `Scene ${frameLabel}`}
                  className="cinematic-film-reel__thumb"
                  draggable={false}
                />
              </button>
              {item.panelImage?.canRegenerate ? (
                <div
                  className="absolute top-1.5 right-1.5 z-10"
                  onClick={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <ImageStoryPanelRetryButton
                    panelImage={item.panelImage}
                    busy={imageRetryBusy === Number(item.frameNumber)}
                    variant="inline"
                    onClick={() => onRetryItem?.(item)}
                    testId={`image-story-reel-regenerate-${item.frameNumber}`}
                  />
                </div>
              ) : null}
            </div>
          ) : (item.panelImage?.canRetry ?? item.canRetryImage) ? (
            <div className="cinematic-film-reel__thumb cinematic-film-reel__thumb--empty cinematic-film-reel__thumb--retry">
              <ImageStoryPanelRetryButton
                panelImage={item.panelImage || { canRetry: true, retryLabel: 'Generate image', panelNumber: item.frameNumber }}
                busy={imageRetryBusy === Number(item.frameNumber)}
                variant="reel"
                onClick={() => onRetryItem?.(item)}
              />
            </div>
          ) : (
            <div className="cinematic-film-reel__thumb cinematic-film-reel__thumb--empty">
              <span className="cinematic-film-reel__thumb-placeholder">FRAME</span>
            </div>
          )}
        </div>

        {!expanded && hasScript ? (
          <p className="cinematic-film-reel__preview">
            {item.sections.find((s) => s.content)?.content}
          </p>
        ) : null}

        {expanded ? (
          <div className="cinematic-film-reel__script">
            {item.sections?.map((section) => (
              section.content ? (
                <details key={section.key} open={section.defaultOpen} className="cinematic-film-reel__script-block">
                  <summary>{section.label}</summary>
                  <div className="cinematic-film-reel__script-body">{section.content}</div>
                </details>
              ) : null
            ))}

            {(onMove || renderExtra) ? (
              <div className="cinematic-film-reel__frame-actions">
                {onMove ? (
                  <div className="cinematic-film-reel__reorder">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={disabled || index === 0}
                      onClick={() => onMove(index, -1)}
                      aria-label="Move scene earlier"
                    >
                      {orientation === 'horizontal' ? (
                        <ChevronLeft className="w-4 h-4" />
                      ) : (
                        <ChevronUp className="w-4 h-4" />
                      )}
                    </Button>
                    <span className="cinematic-film-reel__reorder-label">
                      {index + 1} / {total}
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      disabled={disabled || index >= total - 1}
                      onClick={() => onMove(index, 1)}
                      aria-label="Move scene later"
                    >
                      {orientation === 'horizontal' ? (
                        <ChevronRight className="w-4 h-4" />
                      ) : (
                        <ChevronDown className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                ) : null}
                {renderExtra ? renderExtra(item, index) : null}
              </div>
            ) : null}

            {item.error ? (
              <p className="cinematic-film-reel__error">{item.error}</p>
            ) : null}
          </div>
        ) : null}
      </div>
    </article>
  );
}

/**
 * Cinematic 35mm film-strip scene list — horizontal or vertical scroll.
 */
export function CinematicFilmReelSceneList({
  items = [],
  orientation: controlledOrientation,
  defaultOrientation = 'horizontal',
  onOrientationChange,
  expandedId: controlledExpandedId,
  onExpandedChange,
  onSelectItem,
  onMoveItem,
  disabled = false,
  className = '',
  header = null,
  renderExtra,
  resizable = true,
  panelMaxHeight = 450,
  fullWidth = false,
  onRetryItem,
  imageRetryBusy = null,
}) {
  const [internalExpandedId, setInternalExpandedId] = useState(null);

  const orientation = controlledOrientation ?? defaultOrientation;
  const expandedId = controlledExpandedId ?? internalExpandedId;

  const toggleExpand = useCallback(
    (id) => {
      const next = expandedId === id ? null : id;
      if (controlledExpandedId == null) setInternalExpandedId(next);
      onExpandedChange?.(next);
    },
    [controlledExpandedId, expandedId, onExpandedChange],
  );

  useEffect(() => {
    if (items.length === 1 && expandedId == null) {
      toggleExpand(items[0].id);
    }
  }, [items, expandedId, toggleExpand]);

  const scrollClass = useMemo(
    () => (orientation === 'vertical'
      ? 'cinematic-film-reel--vertical'
      : 'cinematic-film-reel--horizontal'),
    [orientation],
  );

  const stripWrapStyle = useMemo(() => {
    if (!resizable) return undefined;
    if (orientation === 'vertical') {
      return {
        height: panelMaxHeight,
        maxHeight: panelMaxHeight,
        minHeight: panelMaxHeight,
      };
    }
    if (fullWidth) {
      return {
        width: '100%',
        maxHeight: panelMaxHeight,
        height: Math.min(300, panelMaxHeight),
      };
    }
    return {
      maxHeight: panelMaxHeight,
      height: Math.min(300, panelMaxHeight),
    };
  }, [resizable, orientation, fullWidth, panelMaxHeight]);

  if (!items.length) return null;

  const rootClass = [
    'cinematic-film-reel',
    scrollClass,
    fullWidth && orientation === 'horizontal' ? 'cinematic-film-reel--horizontal-full' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={rootClass} data-testid="cinematic-film-reel">
      <div className="cinematic-film-reel__toolbar">
        {header || (
          <p className="cinematic-film-reel__toolbar-title">Scene reel</p>
        )}
      </div>

      <div
        className={`cinematic-film-reel__strip-wrap${resizable ? ' cinematic-film-reel__strip-wrap--resizable' : ''}`}
        style={stripWrapStyle}
      >
        <div className="cinematic-film-reel__strip" data-testid="cinematic-film-reel-scroll">
          <div
            className={`cinematic-film-reel__strip-track cinematic-film-reel__strip-track--${orientation}`}
          >
            {orientation === 'horizontal' ? (
              <div
                className="cinematic-film-reel__perforation-band cinematic-film-reel__perforation-band--horizontal"
                aria-hidden
              />
            ) : (
              <div
                className="cinematic-film-reel__perforation-band cinematic-film-reel__perforation-band--vertical"
                aria-hidden
              />
            )}

            <div className="cinematic-film-reel__strip-body">
              <div className="cinematic-film-reel__edge-label cinematic-film-reel__edge-label--top" aria-hidden>
                GOLD PRX400 · FRAME · CINEMATIC REEL
              </div>
              <div className="cinematic-film-reel__frames">
                {items.map((item, index) => (
                  <FilmReelFrame
                    key={item.id}
                    item={item}
                    index={index}
                    total={items.length}
                    expanded={expandedId === item.id}
                    onToggleExpand={toggleExpand}
                    onSelect={onSelectItem}
                    onMove={onMoveItem}
                    orientation={orientation}
                    disabled={disabled}
                    renderExtra={renderExtra}
                    onRetryItem={onRetryItem}
                    imageRetryBusy={imageRetryBusy}
                  />
                ))}
              </div>
              <div className="cinematic-film-reel__edge-label cinematic-film-reel__edge-label--bottom" aria-hidden>
                TWINNEE · SCENE SCRIPT · {String(items.length).padStart(2, '0')} FRAMES
              </div>
            </div>

            {orientation === 'horizontal' ? (
              <div
                className="cinematic-film-reel__perforation-band cinematic-film-reel__perforation-band--horizontal"
                aria-hidden
              />
            ) : (
              <div
                className="cinematic-film-reel__perforation-band cinematic-film-reel__perforation-band--vertical"
                aria-hidden
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
